import type { ApiScopeRequirement } from '@ipfabric/utils/rbac/ApiScopeRequirement';
import { isApiScopeRequirementAnd } from '@ipfabric/utils/rbac/ApiScopeRequirement';
import type { PermissionScope } from 'utils/hooks/usePermissionScope';

const flattenApiScopeRequirement = (
  requirement: ApiScopeRequirement,
): ApiScopeRequirement[] =>
  isApiScopeRequirementAnd(requirement)
    ? requirement.and.flatMap(flattenApiScopeRequirement)
    : [requirement];

/**
 * Merged items are accessible only to users who may access both original items,
 * so their requirements are combined with `and`.
 */
export const mergeApiScopeRequirements = (
  a: ApiScopeRequirement | undefined,
  b: ApiScopeRequirement | undefined,
): ApiScopeRequirement | undefined => {
  if (!a || !b) {
    return a ?? b;
  }

  const seen = new Set<string>();
  const requirements = [
    ...flattenApiScopeRequirement(b),
    ...flattenApiScopeRequirement(a),
  ].filter((requirement) => {
    const key = JSON.stringify(requirement);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return requirements.length === 1 ? requirements[0] : { and: requirements };
};

export const mergePermissionScopes = (
  a: PermissionScope[] | undefined,
  b: PermissionScope[] | undefined,
): PermissionScope[] | undefined => {
  if (!a || !b) {
    return a ?? b;
  }
  return [...a, ...b.filter((permission) => !a.includes(permission))];
};
